import { Alert, Button, Col, Divider, Drawer, EditButton, Icons, Row, Show, Space, Table, Tag, Tooltip, Typography, notification } from "@pankod/refine-antd";
import { IResourceComponentsProps, useCan, useGetIdentity, useOne, useSelect, useShow } from "@pankod/refine-core";
import { StateButtons } from "components/common";
import { RenderTimelineModal } from "components/common/timelineModal";
import { ShowUserCard } from "components/profile/showUserCard";
import { ManageSawAsset } from "components/sawasset/manageSawAsset";
import { LookupButton } from "components/utils/lookupButton";
import { ObjectMetadata } from "components/utils/objectMetadata";
import dayjs from "dayjs";
import { IBaseProps, IDocument, ILocations, IAssociateDetailsSnapshot, ISawAsset, ISawAssetMetadata, IState, IUser, nextState } from "interfaces";
import { useCallback, useEffect, useMemo, useState, ReactNode } from "react";
import { DATAPROVIDER_CONFIGSET, DATAPROVIDER_LOOKUP, DATAPROVIDER_READ, DATAPROVIDER_UPDATE, RESOURCE_PATH, STALE_DURATION, buildAttachmentUrl } from "scripts/site";

const normalizeIdentifier = (value: unknown): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
  return normalized.length ? normalized : undefined;
};

const buildNormalizedSet = (values?: unknown[]): Set<string> => {
  return new Set(
    (values ?? [])
      .map(normalizeIdentifier)
      .filter((value): value is string => Boolean(value))
  );
};

const collectFieldSetIds = (stateDefinition?: IState, transition?: Partial<nextState>): Set<string> => {
  const transitionFieldSets = (transition as unknown as { fieldSetIds?: unknown[] })?.fieldSetIds ?? [];
  const combined: unknown[] = [
    ...(stateDefinition?.fieldSetIds ?? []),
    ...transitionFieldSets,
  ];
  return buildNormalizedSet(combined);
};

type SawAssetConfigRules = ISawAssetMetadata["config"] & {
  editableStates?: string[];
  canManageRequest?: boolean;
  IdsCanAssignrole?: string[];
  idsCanAssignrole?: string[];
  idsCanAssignRole?: string[];
  associateDetailsRequired?: boolean;
};

const { Title, Text } = Typography;

type DocumentWithLegacyPath = IDocument & {
  url?: string;
  rawUrl?: string;
  filepath?: string;
};

type ReturnProofLink = {
  directUrl?: string;
  attachmentUrl?: string;
} | undefined;

type SnapshotTableRow = IAssociateDetailsSnapshot & {
  key: string;
  assignedTo?: string[];
  deliveryPartner?: string[];
};

const resolveAssignableRoles = (config?: SawAssetConfigRules): string[] => {
  if (!config) {
    return [];
  }

  const candidates: unknown[] = [
    config.roleIdsCanAssign,
    (config as { IdsCanAssignrole?: unknown }).IdsCanAssignrole,
    (config as { idsCanAssignrole?: unknown }).idsCanAssignrole,
    (config as { idsCanAssignRole?: unknown }).idsCanAssignRole,
  ];

  for (const entry of candidates) {
    if (Array.isArray(entry) && entry.length > 0) {
      return entry
        .filter((value): value is string => typeof value === "string" && value.trim().length > 0);
    }
  }

  return [];
};

const formatSnapshotTimestamp = (value?: string): string => {
  if (!value) {
    return "---";
  }
  const parsed = dayjs(value);
  if (parsed.isValid()) {
    return parsed.format("DD MMM YYYY HH:mm");
  }
  return value;
};

const normalizeStringArray = (value?: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const filtered = value.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0);
  return filtered.length > 0 ? filtered : undefined;
};

const hasSnapshotContent = (snapshot?: IAssociateDetailsSnapshot | null): boolean => {
  if (!snapshot) {
    return false;
  }
  const {
    recordedAt,
    recordedBy,
    recordedByName,
    action,
    event,
    tcsEmployeeId,
    projectName,
    deliveryHead,
    associateLocation,
    comments,
  } = snapshot;

  if (recordedAt || recordedBy || recordedByName || action || event) {
    return true;
  }

  const assigned = normalizeStringArray(snapshot.assignedTo);
  const partners = normalizeStringArray(snapshot.deliveryPartner);

  return Boolean(
    assigned?.length
    || partners?.length
    || tcsEmployeeId
    || projectName
    || deliveryHead
    || associateLocation
    || comments
  );
};

// Let Refine infer resource and id from the URL route
export const SawAssetShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow<ISawAsset>({
    dataProviderName: DATAPROVIDER_READ
  });
  const { data, isLoading } = queryResult;
  // Handle case where API returns empty string instead of null/undefined
  const record = (data?.data && typeof data.data === 'object') ? data.data as ISawAsset : null;

  const { data: metadata } = useOne<ISawAssetMetadata>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.METADATA,
    id: RESOURCE_PATH.SAW_ASSET,
    queryOptions: { enabled: true, staleTime: STALE_DURATION }
  });
  
  // const shouldFetchConfigSet = useMemo(() => {
  //   if (!record?.id) {
  //     return false;
  //   }
  //   const availableSets = (metadata?.data as ISawAssetMetadata & { configSets?: unknown[] })?.configSets;
  //   return Array.isArray(availableSets) && availableSets.length > 0;
  // }, [record?.id, metadata?.data]);

  const { data: configSet } = useOne<{
    values?: SawAssetConfigRules;
  }>({
    dataProviderName: DATAPROVIDER_CONFIGSET,
    resource: RESOURCE_PATH.SAW_ASSET,
    id: record?.id ?? "",
    queryOptions: {
      enabled: !!record?.id,
      retry: false,
    }
  });
  const metadataConfig = metadata?.data?.config as SawAssetConfigRules | undefined;
  const configSetValues = configSet?.data?.values;
  const effectiveConfig = useMemo(() => ({
    ...(metadataConfig ?? {}),
    ...(configSetValues ?? {}),
  }) as SawAssetConfigRules, [metadataConfig, configSetValues]);

  const canEdit = useCan({
    resource: RESOURCE_PATH.SAW_ASSET,
    action: "edit",
    params: {
      dataProviderName: DATAPROVIDER_UPDATE,
      id: record?.id,
    },
    queryOptions: {
      enabled: !!record?.id,
    }
  });
  const { data: userData } = useGetIdentity();
  const userObj = userData as IUser;
  const accessoriesOptions = (effectiveConfig?.accessories ?? []).filter((item): item is string => !!item);
  const makeModelOptions = (effectiveConfig?.makeModel ?? []).filter((item): item is string => !!item);
  const resolutionOptions = (effectiveConfig?.resolution ?? []).filter((item): item is string => !!item);
  const assignableRoles = useMemo(() => resolveAssignableRoles(effectiveConfig), [effectiveConfig]);
  const userRoleIds = useMemo(
    () => (Array.isArray(userObj?.entityRoleIds) ? userObj.entityRoleIds : []),
    [userObj?.entityRoleIds]
  );
  const canAssign = useMemo(
    () => assignableRoles.length === 0 || assignableRoles.some((role) => userRoleIds.includes(role)),
    [assignableRoles, userRoleIds]
  );
  // Load locations to resolve MS Inventory Location name
  const { queryResult: locationProps } = useSelect<ILocations>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.LOCATION,
    optionLabel: "name",
    optionValue: "id",
    sort: [{ field: "name", order: "asc" }],
    fetchSize: 100
  });
  const allLocations = locationProps.data?.data;
  const resolveLocationName = useCallback((locationId?: string | null) => {
    if (!locationId) {
      return "---";
    }
    const match = allLocations?.find((loc) => loc.id === locationId);
    return match?.name ?? locationId ?? "---";
  }, [allLocations]);
  const msInventoryLocationName = resolveLocationName(record?.msInventoryLocation);

  const accessoryValues = useMemo<string[]>(() => {
    const accessories = record?.accessories;
    if (!Array.isArray(accessories)) {
      return [];
    }
    return accessories.filter(Boolean) as string[];
  }, [record?.accessories]);
  const showAccessoriesSection = accessoriesOptions.length > 0;
  const hasMakeModel = !!record?.makeModel || makeModelOptions.length > 0;
  const hasResolution = !!record?.resolution || resolutionOptions.length > 0;
  const showConfigurationSection = hasMakeModel || hasResolution;
  const configurationDetails = (
    <Space direction="vertical" size={12}>
      {hasMakeModel ? (
        <div>
          <Title level={5}>Make/Model</Title>
          <Text>{record?.makeModel ?? "---"}</Text>
        </div>
      ) : null}
      {hasResolution ? (
        <div>
          <Title level={5}>Resolution</Title>
          <Text>{record?.resolution ?? "---"}</Text>
        </div>
      ) : null}
    </Space>
  );

  const [refetchData, switchRefetchFlag] = useState(false);
  const [manageDrawerOpen, setManageDrawerOpen] = useState(false);
  const [isFetchingReturnProof, setIsFetchingReturnProof] = useState(false);

  const refetchSawAssetData = () => {
    switchRefetchFlag(true);
  }


  useEffect(() => {
    if (refetchData) {
      queryResult.refetch();
      switchRefetchFlag(false);
    }
  }, [refetchData, queryResult]);

  const metadataStates = useMemo(() => metadata?.data?.states ?? [], [metadata?.data?.states]);
  const stateDefinitionById = useMemo(() => {
    const lookup = new Map<string, IState>();
    metadataStates.forEach((state) => {
      const normalizedState = normalizeIdentifier(state.state);
      if (normalizedState) {
        lookup.set(normalizedState, state);
      }
    });
    return lookup;
  }, [metadataStates]);
  const resolveStateLabel = useCallback(
    (stateId?: string): string | undefined => {
      if (!stateId) {
        return undefined;
      }
      const normalized = normalizeIdentifier(stateId);
      if (!normalized) {
        return stateId;
      }
      const definition = stateDefinitionById.get(normalized);
      if (!definition) {
        return stateId;
      }
      return definition.displayName ?? definition.state ?? stateId;
    },
    [stateDefinitionById]
  );
  const normalizedCurrentState = useMemo(
    () => normalizeIdentifier(record?.stateManager?.state),
    [record?.stateManager?.state]
  );
  const currentStateDefinition = useMemo(
    () => (normalizedCurrentState ? stateDefinitionById.get(normalizedCurrentState) : undefined),
    [stateDefinitionById, normalizedCurrentState]
  );
  const currentStateFieldSets = useMemo(
    () => collectFieldSetIds(currentStateDefinition),
    [currentStateDefinition]
  );
  const hasReturnProofFieldSet = useMemo(
    () => currentStateFieldSets.has("returnproofdocument"),
    [currentStateFieldSets]
  );
  const isReturnToMsState = hasReturnProofFieldSet;
  const inventoryStateSet = useMemo(() => {
    const configuredStates = Array.isArray(metadata?.data?.config?.inventoryStates)
      ? metadata?.data?.config?.inventoryStates
      : [];
    return buildNormalizedSet(configuredStates);
  }, [metadata?.data?.config?.inventoryStates]);
  const isInventoryState = useMemo(
    () => (normalizedCurrentState ? inventoryStateSet.has(normalizedCurrentState) : false),
    [inventoryStateSet, normalizedCurrentState]
  );
  const currentStateMetadata = useMemo(
    () => metadataStates.find((state) => normalizeIdentifier(state.state) === normalizedCurrentState),
    [metadataStates, normalizedCurrentState]
  );
  const assignedUsers = useMemo(() => {
    const managerAssignments = Array.isArray(record?.stateManager?.assignedTo)
      ? (record?.stateManager?.assignedTo ?? [])
      : [];
    const fallbackValue = record?.assignedTo;
    const fallbackAssignment = managerAssignments.length === 0 && typeof fallbackValue === "string"
      ? [fallbackValue]
      : [];
    const combined = [...managerAssignments, ...fallbackAssignment];
    return combined.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0);
  }, [record?.stateManager?.assignedTo, record?.assignedTo]);
  const configuredHiddenActions = useMemo(
    () => (effectiveConfig?.stateButtonsHideActions ?? [])
      .filter((action): action is string => typeof action === "string" && action.trim().length > 0),
    [effectiveConfig?.stateButtonsHideActions]
  );
  const filteredHiddenActions = useMemo(
    () => configuredHiddenActions.filter((action) => normalizeIdentifier(action) !== "return"),
    [configuredHiddenActions]
  );
  const associateDetailsRequired = effectiveConfig?.associateDetailsRequired !== false;
  const manageActions = useMemo(() => {
    if (!currentStateMetadata?.nextStates?.length) {
      return [] as nextState[];
    }

    const hiddenActionSet = new Set(
      filteredHiddenActions
        .map(normalizeIdentifier)
        .filter((action): action is string => Boolean(action))
    );

    if (hiddenActionSet.size === 0) {
      return [] as nextState[];
    }

    return currentStateMetadata.nextStates.filter((next) => {
      const normalizedAction = normalizeIdentifier(next.action);
      return Boolean(normalizedAction && hiddenActionSet.has(normalizedAction));
    });
  }, [filteredHiddenActions, currentStateMetadata?.nextStates]);

  const hideActions = filteredHiddenActions;
  const allowManage = effectiveConfig?.canManageRequest ?? true;
  const shouldShowManage = allowManage && canAssign && manageActions.length > 0;
  const editableStateSet = useMemo(() => buildNormalizedSet(effectiveConfig?.editableStates), [effectiveConfig?.editableStates]);
  const isEditableState = editableStateSet.size === 0 || (normalizedCurrentState ? editableStateSet.has(normalizedCurrentState) : true);
  const rawState = record?.stateManager?.state;
  const stateLabel = currentStateMetadata?.displayName
    ?? (rawState ?? "Loading");
  const primaryReturnProof = useMemo<DocumentWithLegacyPath | undefined>(() => {
    const documents = record?.returnProofDocument;
    if (!Array.isArray(documents) || documents.length === 0) {
      return undefined;
    }
    return documents[0] as DocumentWithLegacyPath;
  }, [record?.returnProofDocument]);
  const resolveReturnProofLink = useCallback(
    (doc?: DocumentWithLegacyPath): ReturnProofLink => {
      if (!doc) {
        return undefined;
      }
      const candidatePath = doc.response?.url ?? doc.url ?? doc.rawUrl ?? doc.filepath;
      if (!candidatePath) {
        return undefined;
      }
      if (candidatePath.startsWith("http")) {
        return { directUrl: candidatePath };
      }
      if (!record?.id) {
        return undefined;
      }
      return { attachmentUrl: buildAttachmentUrl(RESOURCE_PATH.SAW_ASSET, record.id, candidatePath) };
    },
    [record?.id]
  );
  const primaryReturnProofLink = useMemo(
    () => resolveReturnProofLink(primaryReturnProof),
    [primaryReturnProof, resolveReturnProofLink]
  );
  const hasReturnProof = Boolean(primaryReturnProofLink);
  const shouldDisplayReturnProofSection = hasReturnProofFieldSet || hasReturnProof;
  const deliveryPartnerValue = record?.deliveryPartner;
  const deliveryPartners = Array.isArray(deliveryPartnerValue)
    ? deliveryPartnerValue.filter(Boolean)
    : (deliveryPartnerValue ? [deliveryPartnerValue] : []);
  const primaryDeliveryPartnerId = deliveryPartners[0];
  const tcsBranchName = resolveLocationName(record?.tcsBranch);
  const associateLocationName = resolveLocationName(record?.associateLocation);
  const hasMeaningfulValue = (value: unknown): boolean => {
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    if (Array.isArray(value)) {
      return value.some((entry) => hasMeaningfulValue(entry));
    }
    return Boolean(value);
  };

  const hasAssignmentValues =
    assignedUsers.length > 0 ||
    hasMeaningfulValue(record?.tcsEmployeeId) ||
    hasMeaningfulValue(record?.projectName) ||
    hasMeaningfulValue(record?.associateLocation) ||
    hasMeaningfulValue(record?.deliveryHead) ||
    deliveryPartners.length > 0;

  const currentSnapshotRows = useMemo<SnapshotTableRow[]>(() => {
    if (!record?.current || !hasSnapshotContent(record.current)) {
      return [];
    }
    return [
      {
        key: "current-0",
        ...record.current,
        assignedTo: normalizeStringArray(record.current.assignedTo),
        deliveryPartner: normalizeStringArray(record.current.deliveryPartner),
      },
    ];
  }, [record]);

  const previousSnapshotRows = useMemo<SnapshotTableRow[]>(() => {
    if (!record?.previous || !Array.isArray(record.previous)) {
      return [];
    }
    return record.previous
      .map((snapshot, index) => {
        if (!snapshot || !hasSnapshotContent(snapshot)) {
          return null;
        }
        return {
          key: `previous-${index}`,
          ...snapshot,
          assignedTo: normalizeStringArray(snapshot.assignedTo),
          deliveryPartner: normalizeStringArray(snapshot.deliveryPartner),
        } as SnapshotTableRow;
      })
      .filter((entry): entry is SnapshotTableRow => Boolean(entry));
  }, [record]);

  const historySnapshotRows = useMemo<SnapshotTableRow[]>(() => {
    if (!record?.history || !Array.isArray(record.history)) {
      return [];
    }
    const rows = record.history
      .map((snapshot, index) => {
        if (!snapshot || !hasSnapshotContent(snapshot)) {
          return null;
        }
        return {
          key: `history-${index}`,
          ...snapshot,
          assignedTo: normalizeStringArray(snapshot.assignedTo),
          deliveryPartner: normalizeStringArray(snapshot.deliveryPartner),
        } as SnapshotTableRow;
      })
      .filter((entry): entry is SnapshotTableRow => Boolean(entry));

    const parseTime = (value?: string) => {
      if (!value) {
        return undefined;
      }
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed : undefined;
    };

    return rows.sort((left, right) => {
      const rightTime = parseTime(right.recordedAt);
      const leftTime = parseTime(left.recordedAt);
      if (rightTime && leftTime) {
        return rightTime.valueOf() - leftTime.valueOf();
      }
      if (rightTime) {
        return 1;
      }
      if (leftTime) {
        return -1;
      }
      return right.key.localeCompare(left.key);
    });
  }, [record]);

  const latestPreviousSnapshot = useMemo(() => previousSnapshotRows[0], [previousSnapshotRows]);
  const previousAssignedUsers = latestPreviousSnapshot?.assignedTo ?? [];
  const previousTcsEmployeeId = latestPreviousSnapshot?.tcsEmployeeId ?? undefined;
  const previousProjectName = latestPreviousSnapshot?.projectName ?? undefined;
  const previousDeliveryHeadId = latestPreviousSnapshot?.deliveryHead ?? undefined;
  const previousDeliveryPartnerIds = latestPreviousSnapshot?.deliveryPartner ?? [];
  const previousPrimaryDeliveryPartnerId = previousDeliveryPartnerIds[0];
  const previousAssociateLocationName = latestPreviousSnapshot
    ? resolveLocationName(latestPreviousSnapshot.associateLocation)
    : "---";
  const hasPreviousSummary = Boolean(latestPreviousSnapshot);
  const showPreviousSummary = useMemo(() => {
    if (!hasPreviousSummary) {
      return false;
    }
    if (isInventoryState || isReturnToMsState) {
      return false;
    }
    return associateDetailsRequired;
  }, [associateDetailsRequired, hasPreviousSummary, isInventoryState, isReturnToMsState]);

  const hasSnapshotTables = currentSnapshotRows.length > 0
    || previousSnapshotRows.length > 0
    || historySnapshotRows.length > 0;

  const renderUserList = (values?: string[]): ReactNode => {
    if (!values || values.length === 0) {
      return <Text>---</Text>;
    }
    return (
      <Space direction="vertical" size={4}>
        {values.map((userId, index) => (
          <ShowUserCard key={`${userId}-${index}`} id={userId} />
        ))}
      </Space>
    );
  };

  const renderSnapshotColumns = (): ReactNode => (
    <>
      <Table.Column<SnapshotTableRow>
        title="Recorded At"
        dataIndex="recordedAt"
        key="recordedAt"
        render={(value: string | undefined) => <Text>{formatSnapshotTimestamp(value)}</Text>}
      />
      <Table.Column<SnapshotTableRow>
        title="Recorded By"
        dataIndex="recordedBy"
        key="recordedBy"
        render={(value: string | undefined, row) => {
          if (value) {
            return <ShowUserCard id={value} />;
          }
          if (row.recordedByName) {
            return <Text>{row.recordedByName}</Text>;
          }
          return <Text>---</Text>;
        }}
      />
      <Table.Column<SnapshotTableRow>
        title="State"
        dataIndex="stateId"
        key="stateId"
        render={(value: string | undefined) => <Text>{resolveStateLabel(value) ?? value ?? "---"}</Text>}
      />
      <Table.Column<SnapshotTableRow>
        title="Assigned To"
        dataIndex="assignedTo"
        key="assignedTo"
        render={(value: string[] | undefined) => renderUserList(value)}
      />
      <Table.Column<SnapshotTableRow>
        title="Name / Email"
        dataIndex="nameEmail"
        key="nameEmail"
        render={(value: string | undefined) => <Text>{value ?? "---"}</Text>}
      />
      <Table.Column<SnapshotTableRow>
        title="TCS Employee Id"
        dataIndex="tcsEmployeeId"
        key="tcsEmployeeId"
        render={(value: string | undefined) => <Text>{value ?? "---"}</Text>}
      />
      <Table.Column<SnapshotTableRow>
        title="Project Name"
        dataIndex="projectName"
        key="projectName"
        render={(value: string | undefined) => <Text>{value ?? "---"}</Text>}
      />
      <Table.Column<SnapshotTableRow>
        title="Delivery Head (L1)"
        dataIndex="deliveryHead"
        key="deliveryHead"
        render={(value: string | undefined) => (value ? <ShowUserCard id={value} /> : <Text>---</Text>)}
      />
      <Table.Column<SnapshotTableRow>
        title="Delivery Partner (L2)"
        dataIndex="deliveryPartner"
        key="deliveryPartner"
        render={(value: string[] | undefined) => renderUserList(value)}
      />
      <Table.Column<SnapshotTableRow>
        title="Associate Location"
        dataIndex="associateLocation"
        key="associateLocation"
        render={(value: string | undefined) => <Text>{value ?? "---"}</Text>}
      />
      <Table.Column<SnapshotTableRow>
        title="Comments"
        dataIndex="comments"
        key="comments"
        render={(value: string | undefined) => <Text style={{ whiteSpace: "pre-wrap" }}>{value ?? "---"}</Text>}
      />
    </>
  );

  const renderSnapshotTable = (title: string, rows: SnapshotTableRow[]): ReactNode => {
    if (!rows.length) {
      return null;
    }
    return (
      <div key={title}>
        <Title level={5}>{title}</Title>
        <Table<SnapshotTableRow>
          size="small"
          bordered
          pagination={false}
          rowKey="key"
          dataSource={rows}
          scroll={{ x: 1200 }}
        >
          {renderSnapshotColumns()}
        </Table>
      </div>
    );
  };

  const snapshotTablesSection = hasSnapshotTables ? (
    <>
      <Divider orientation="left">Assignment Change Log</Divider>
      {renderSnapshotTable("Current Assignment", currentSnapshotRows)}
      {renderSnapshotTable("Previous Assignments", previousSnapshotRows)}
      {renderSnapshotTable("Assignment History", historySnapshotRows)}
    </>
  ) : null;
  const resolveAttachmentSas = useCallback(async (attachmentUrl: string): Promise<string | undefined> => {
    try {
      const absoluteUrl = new URL(attachmentUrl, window.location.origin);
      absoluteUrl.searchParams.set("redirect", "false");

      const response = await fetch(absoluteUrl.toString(), {
        credentials: "include",
      });

      if (!response.ok) {
        return undefined;
      }

      const payload: { url?: string } | undefined = await response.json().catch(() => undefined);
      return typeof payload?.url === "string" ? payload.url : undefined;
    } catch (error) {
      console.error("Failed to resolve attachment SAS", error);
      return undefined;
    }
  }, []);

  const handleOpenReturnProof = useCallback(async () => {
    if (!primaryReturnProofLink) {
      return;
    }

    if (primaryReturnProofLink.directUrl) {
      window.open(primaryReturnProofLink.directUrl, "_blank", "noopener");
      return;
    }

    if (!primaryReturnProofLink.attachmentUrl) {
      notification.error({
        message: "Return proof unavailable",
        description: "We couldn't find the attachment path for this record.",
      });
      return;
    }

    setIsFetchingReturnProof(true);
    try {
      const sasUrl = await resolveAttachmentSas(primaryReturnProofLink.attachmentUrl);
      if (sasUrl) {
        window.open(sasUrl, "_blank", "noopener");
      } else {
        notification.error({
          message: "Unable to open document",
          description: "We couldn't retrieve the return proof attachment. Please try again or contact support.",
        });
      }
    } finally {
      setIsFetchingReturnProof(false);
    }
  }, [primaryReturnProofLink, resolveAttachmentSas]);

  const showAssociateDetails = useMemo(() => {
    if (!hasAssignmentValues) {
      return false;
    }
    if (isInventoryState || isReturnToMsState) {
      return false;
    }
    return associateDetailsRequired;
  }, [associateDetailsRequired, hasAssignmentValues, isInventoryState, isReturnToMsState]);

  // const manageActions = metadata?.data?.states?.find(st =>
  //   st.state === record?.stateManager?.state
  // )?.nextStates; // manage actions disabled

  // Show not found message if API returns empty string or no record
  if (!isLoading && (!record || typeof data?.data === 'string')) {
    return (
      <Show isLoading={false}>
        <Alert
          type="warning"
          message="SAW Asset not found"
          description={`Record not found for this ID. The API returned: ${JSON.stringify(data?.data)}`}
        />
      </Show>
    );
  }

  // Responsive layout configuration - matching cloud screening and create page pattern
  const twoColumnLayout = {
    xs: { span: 24 },
    sm: { span: 24 },
    md: { span: 12 },
    lg: { span: 12 },
    xl: { span: 12 },
    xxl: { span: 12 },
  };

  const rowGutter = { xs: 8, sm: 16, md: 24, lg: 32, xl: 40, xxl: 48 };
  const displayAssetId = record?.sawAssetId ?? record?.id ?? "---";

  return (
    // Removed incomplete and unclosed tags: <Tag ...> line. Only the correct version remains below.
    <Show
      isLoading={isLoading}
      headerProps={{
        title: displayAssetId === "---" ? "SAW Asset" : `SAW Asset ${displayAssetId}`,
        backIcon: true,
        tags: (
          <Tag color={currentStateMetadata?.color ?? "default"}>
            {stateLabel}
          </Tag>
        ),
        extra:
          !isLoading && record && (
            <Space>
              {shouldShowManage && (
                <Button
                  type="primary"
                  key="manage"
                  onClick={() => setManageDrawerOpen(true)}
                  icon={<Icons.FormOutlined />}
                >
                  {manageActions.length === 1 ? manageActions[0]?.buttonText : "Manage"}
                </Button>
              )}
              <StateButtons
                {...(record as IBaseProps)}
                resourceToAct={RESOURCE_PATH.SAW_ASSET}
                resourceToActId={record?.id || "0"}
                resourceToNavigate={RESOURCE_PATH.PROFILE}
                hideActions={hideActions}
                skipAssignedToCheck={canAssign}
                customPostStateAction={() => {
                  refetchSawAssetData();
                }}
                appendFormValues={(values, stateAction) => {
                  // If returning to IS, remove associate assignment and values
                  if (stateAction === "return") {
                    return {
                      //assignedTo: [],
                      tcsEmployeeId: "",
                      projectName: "",
                      associateLocation: "",
                      deliveryHead: "",
                      deliveryPartner: []
                    };
                  }
                  return {};
                }}

              />
              {canEdit.data?.can && !isReturnToMsState && isEditableState && <EditButton />}
              <RenderTimelineModal
                stateManager={record.stateManager}
                entityId={record?.id}
                entityName={RESOURCE_PATH.SAW_ASSET}
              />
            </Space>
          ),
      }}
    >

      <>
        <Drawer
          title="Manage SAW Asset"
          placement="right"
          size="default"
          open={manageDrawerOpen}
          onClose={() => setManageDrawerOpen(false)}
        >
          {metadata?.data && record && (
            <ManageSawAsset
              sawAssetRecord={record}
              sawAssetMetadata={metadata.data}
              onFormSubmission={() => setManageDrawerOpen(false)}
              refetch={refetchSawAssetData}
              actions={manageActions}
            />
          )}
        </Drawer>

        {showAssociateDetails && (
          <>
            <Divider orientation="left">Associate Details</Divider>
            <Row gutter={rowGutter} style={{ marginTop: 15 }}>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>Emp Name (Allocated to)</Title>
                {assignedUsers.length > 0
                  ? assignedUsers.map((id, index) => <ShowUserCard key={id || index} id={id} />)
                  : <Text>---</Text>}
              </Col>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>TCS Employee Id</Title>
                <Text copyable={!!record?.tcsEmployeeId}>{record?.tcsEmployeeId ?? "---"}</Text>
              </Col>
            </Row>
            <Row gutter={rowGutter} style={{ marginTop: 15 }}>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>Project Name</Title>
                <Text>{record?.projectName ?? "---"}</Text>
              </Col>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>Associate Location</Title>
                <Text>{associateLocationName}</Text>
              </Col>
            </Row>
            <Row gutter={rowGutter} style={{ marginTop: 15 }}>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>Delivery Head (L1)</Title>
                {!!record?.deliveryHead ? (
                  <ShowUserCard id={record.deliveryHead} />
                ) : (
                  <Text>---</Text>
                )}
              </Col>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>Delivery Partner (L2)</Title>
                {primaryDeliveryPartnerId ? (
                  <ShowUserCard id={primaryDeliveryPartnerId} />
                ) : (
                  <Text>---</Text>
                )}
              </Col>
            </Row>
          </>
        )}

        {showPreviousSummary && (
          <>
            <Divider orientation="left">Previous Assignment</Divider>
            <Row gutter={rowGutter} style={{ marginTop: 15 }}>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>Emp Name (Allocated to)</Title>
                {previousAssignedUsers.length > 0
                  ? previousAssignedUsers.map((id, index) => <ShowUserCard key={id || index} id={id} />)
                  : <Text>---</Text>}
              </Col>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>TCS Employee Id</Title>
                <Text copyable={!!previousTcsEmployeeId}>{previousTcsEmployeeId ?? "---"}</Text>
              </Col>
            </Row>
            <Row gutter={rowGutter} style={{ marginTop: 15 }}>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>Project Name</Title>
                <Text>{previousProjectName ?? "---"}</Text>
              </Col>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>Associate Location</Title>
                <Text>{previousAssociateLocationName}</Text>
              </Col>
            </Row>
            <Row gutter={rowGutter} style={{ marginTop: 15 }}>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>Delivery Head (L1)</Title>
                {previousDeliveryHeadId
                  ? <ShowUserCard id={previousDeliveryHeadId} />
                  : <Text>---</Text>}
              </Col>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>Delivery Partner (L2)</Title>
                {previousPrimaryDeliveryPartnerId
                  ? <ShowUserCard id={previousPrimaryDeliveryPartnerId} />
                  : <Text>---</Text>}
              </Col>
            </Row>
          </>
        )}

        {/* Asset Information Section */}
        <Divider orientation="left">Asset Information</Divider>
        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>SAW Asset ID</Title>
            <Text copyable={displayAssetId !== "---"}>{displayAssetId}</Text>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>Serial Number</Title>
            <Text copyable={!!record?.serialNumber}>{record?.serialNumber}</Text>
          </Col>
        </Row>
        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>PO Details</Title>
            <Text copyable={!!record?.poDetails}>{record?.poDetails ?? "---"}</Text>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>Date of Delivery</Title>
            <Text>{record?.dateOfDelivery ? dayjs(record.dateOfDelivery).format("DD/MM/YYYY") : "---"}</Text>
          </Col>
        </Row>
        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>Value of the Device</Title>
            <Text copyable={!!record?.deviceValue}>{record?.deviceValue ?? "---"}</Text>
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>Dock Serial Number</Title>
            <Text copyable={!!record?.dockSerialNumber}>{record?.dockSerialNumber ?? "---"}</Text>
          </Col>
        </Row>

        {showAccessoriesSection && (
          <Row gutter={rowGutter} style={{ marginTop: 15 }}>
            <Col className="gutter-row" {...twoColumnLayout}>
              <Title level={5}>Accessories</Title>
              {accessoryValues.length > 0 ? (
                <Space wrap split={<Divider type="vertical" />}>
                  {accessoryValues.map((acc, i) => (
                    <Tag key={acc || i}>{acc}</Tag>
                  ))}
                </Space>
              ) : (
                <Text>---</Text>
              )}
            </Col>
            {showConfigurationSection && (
              <Col className="gutter-row" {...twoColumnLayout}>
                {configurationDetails}
              </Col>
            )}
          </Row>
        )}

        {!showAccessoriesSection && showConfigurationSection && (
          <Row gutter={rowGutter} style={{ marginTop: 15 }}>
            <Col className="gutter-row" {...twoColumnLayout}>
              {configurationDetails}
            </Col>
          </Row>
        )}

        {/* Location Information Section */}
        <Divider orientation="left">Location</Divider>
        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>Country</Title>
            {record?.countryId ? (
              <LookupButton id={record.countryId} resource={RESOURCE_PATH.COUNTRY} noClick />
            ) : (
              <Text>---</Text>
            )}
          </Col>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>MS Inventory Location</Title>
            <Text>{msInventoryLocationName}</Text>
          </Col>
        </Row>
        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
          <Col className="gutter-row" {...twoColumnLayout}>
            <Title level={5}>TCS Branch</Title>
            <Text>{tcsBranchName}</Text>
          </Col>
        </Row>
        {shouldDisplayReturnProofSection && (
          <>
            <Divider orientation="left">Return to MS</Divider>
            <Row gutter={rowGutter} style={{ marginTop: 15 }}>
              <Col className="gutter-row" {...twoColumnLayout}>
                <Title level={5}>Return proof document</Title>
                {hasReturnProof ? (
                  <Tooltip title={primaryReturnProof?.name ?? "Document"} placement="topLeft">
                    <Button
                      icon={<Icons.FileOutlined />}
                      onClick={handleOpenReturnProof}
                      loading={isFetchingReturnProof}
                      disabled={isFetchingReturnProof}
                    >
                      {primaryReturnProof?.name ?? "Document"}
                    </Button>
                  </Tooltip>
                ) : (
                  <Text>---</Text>
                )}
              </Col>
            </Row>
          </>
        )}
        <div className="mt-15">
          {record && <ObjectMetadata {...(record as IBaseProps)} />}
        </div>

        {snapshotTablesSection}
      </>
    </Show>
  );
}
